import { useQuery, useQueries } from '@tanstack/react-query'
import { useAuth } from '@/modules/auth'
import { TripService } from '@/modules/trips/services/tripService'
import { SubscriptionsApi } from '@/shared/api/subscriptions'
import type { TripWithDetailsResponse } from '@/shared/api/types'
import type { SubscriptionStatus } from '@/shared/api/subscriptions'
import { useMemo } from 'react'

interface UseTripDataOptions {
  enabled?: boolean
  includeSubscription?: boolean
}

interface UseTripDataReturn {
  trip: TripWithDetailsResponse | undefined
  subscription: SubscriptionStatus | undefined
  meals: any[]
  gear: any[]
  waypoints: any[]
  members: any[]
  isLoading: boolean
  isTripLoading: boolean
  isSubscriptionLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Unified hook for fetching trip data with React Query.
 * Fetches trip details (with embedded meals, gear, waypoints, members) and optionally subscription status in parallel.
 * 
 * @param tripId - The trip ID to fetch
 * @param options - Configuration options
 * @returns Trip data, subscription status, and loading/error states
 */
export function useTripData(
  tripId: string,
  options: UseTripDataOptions = {}
): UseTripDataReturn {
  const { enabled = true, includeSubscription = true } = options
  const { getAccessToken, isAuthenticated } = useAuth()

  // Create service instances
  const tripService = useMemo(() => new TripService(getAccessToken), [getAccessToken])
  const subscriptionApi = useMemo(() => new SubscriptionsApi(getAccessToken), [getAccessToken])

  // Fetch trip data and subscription in parallel
  const queries = useQueries({
    queries: [
      {
        queryKey: ['trip', tripId],
        queryFn: () => tripService.getTrip(tripId),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        enabled: enabled && !!tripId && isAuthenticated,
        retry: 1
      },
      {
        queryKey: ['subscription'],
        queryFn: () => subscriptionApi.getStatus(),
        staleTime: 30 * 60 * 1000, // 30 minutes - subscriptions don't change often
        gcTime: 60 * 60 * 1000, // 1 hour
        enabled: includeSubscription && isAuthenticated,
        retry: 1
      }
    ]
  })

  const [tripQuery, subscriptionQuery] = queries

  // Extract data from trip response
  const trip = tripQuery.data
  const meals = trip?.meals || []
  const gear = trip?.gear || []
  const waypoints = trip?.waypoints || []
  const members = trip?.members || []

  // Combine loading states
  const isTripLoading = tripQuery.isLoading
  const isSubscriptionLoading = includeSubscription ? subscriptionQuery.isLoading : false
  const isLoading = isTripLoading || isSubscriptionLoading

  // Combine errors
  const error = (tripQuery.error || subscriptionQuery.error) as Error | null

  // Refetch function
  const refetch = () => {
    tripQuery.refetch()
    if (includeSubscription) {
      subscriptionQuery.refetch()
    }
  }

  return {
    trip,
    subscription: subscriptionQuery.data,
    meals,
    gear,
    waypoints,
    members,
    isLoading,
    isTripLoading,
    isSubscriptionLoading,
    error,
    refetch
  }
}

/**
 * Simplified hook for fetching only trip data without subscription.
 * Use this when you don't need subscription information.
 */
export function useTripOnly(tripId: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options
  const { getAccessToken, isAuthenticated } = useAuth()

  const tripService = useMemo(() => new TripService(getAccessToken), [getAccessToken])

  const query = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => tripService.getTrip(tripId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: enabled && !!tripId && isAuthenticated,
    retry: 1
  })

  const trip = query.data

  return {
    trip,
    meals: trip?.meals || [],
    gear: trip?.gear || [],
    waypoints: trip?.waypoints || [],
    members: trip?.members || [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch
  }
}
